import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Save } from "lucide-react";
import {
  listarDistribuidoresDropdown,
  listarModulos,
  obterTabelaPreco,
  criarTabelaPreco,
  atualizarTabelaPreco,
} from "../services/api";
import {
  UNIDADES_COBRANCA,
  formatValorPrecoInput,
  linhasPrecoPorSecoes,
  parseValorPreco,
  unidadePadraoPreco,
} from "../utils/tabelaPrecoLicencas";

const FormularioTabelaPreco = ({ token }) => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);

  const [nome, setNome] = useState("");
  const [descricao, setDescricao] = useState("");
  const [ativo, setAtivo] = useState(true);
  const [distribuidorId, setDistribuidorId] = useState("");
  const [distribuidores, setDistribuidores] = useState([]);
  const [modulos, setModulos] = useState([]);
  const [precos, setPrecos] = useState({});
  const [unidades, setUnidades] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const blocos = useMemo(() => linhasPrecoPorSecoes(modulos), [modulos]);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const modResp = await listarModulos(token, { ativo: true });
        const mods = Array.isArray(modResp?.data) ? modResp.data : [];
        setModulos(mods.sort((a, b) => (a.ordem || 0) - (b.ordem || 0)));

        const precosInit = {};
        const unidInit = {};

        linhasPrecoPorSecoes(mods).forEach((bloco) => {
          bloco.linhas.forEach(({ modulo, secao }) => {
            precosInit[modulo.id] = "";
            unidInit[modulo.id] = unidadePadraoPreco(modulo.codigo, secao.licencaTipo);
          });
        });

        if (isEdit) {
          const tResp = await obterTabelaPreco(id, token);
          const t = tResp?.data;
          setNome(t?.nome || "");
          setDescricao(t?.descricao || "");
          setAtivo(t?.ativo !== false);
          setDistribuidorId(t?.distribuidorId ? String(t.distribuidorId) : "");
          (t?.itens || []).forEach((item) => {
            const modId = item.moduloId ?? item.ModuloId;
            const codigo = item.moduloCodigo ?? item.ModuloCodigo ?? "";
            precosInit[modId] = formatValorPrecoInput(item.valorUnitario);
            unidInit[modId] =
              item.unidadeCobranca ||
              unidadePadraoPreco(codigo, inferLicencaTipoPorCodigo(codigo));
          });
        }

        setPrecos(precosInit);
        setUnidades(unidInit);
      } catch (e) {
        console.error(e);
        alert("Erro ao carregar dados da tabela.");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [token, id, isEdit]);

  useEffect(() => {
    listarDistribuidoresDropdown(token)
      .then((r) => setDistribuidores(Array.isArray(r.data) ? r.data : []))
      .catch(() => setDistribuidores([]));
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!nome.trim()) {
      alert("Informe o nome da tabela.");
      return;
    }

    const moduloIds = new Set();
    const itens = [];
    blocos.forEach((bloco) => {
      bloco.linhas.forEach(({ modulo, secao }) => {
        if (moduloIds.has(modulo.id)) return;
        const valor = parseValorPreco(precos[modulo.id]);
        if (valor <= 0) return;
        moduloIds.add(modulo.id);
        itens.push({
          moduloId: modulo.id,
          valorUnitario: valor,
          unidadeCobranca: unidades[modulo.id] || unidadePadraoPreco(modulo.codigo, secao.licencaTipo),
        });
      });
    });

    const payload = {
      nome: nome.trim(),
      descricao: descricao.trim(),
      ativo,
      distribuidorId: distribuidorId ? Number(distribuidorId) : null,
      itens,
    };

    setSaving(true);
    try {
      if (isEdit) {
        await atualizarTabelaPreco(id, payload, token);
      } else {
        await criarTabelaPreco(payload, token);
      }
      alert("Tabela salva com sucesso!");
      navigate("/tabelas-preco");
    } catch (err) {
      const data = err?.response?.data;
      alert(typeof data === "string" ? data : data?.message || "Erro ao salvar tabela.");
    } finally {
      setSaving(false);
    }
  };

  const renderLinha = (linha) => {
    const { modulo, tipo, secao } = linha;
    const isBase = tipo === "base";
    return (
      <div
        key={modulo.id}
        className={`
          grid grid-cols-1 md:grid-cols-12 gap-3 items-center py-3 border-b border-gray-50 dark:border-white/5
          ${isBase ? "bg-blue-50/40 dark:bg-blue-500/5 rounded-xl px-2 -mx-2" : ""}
        `}
      >
        <div className="md:col-span-5">
          <p className="text-sm font-medium text-gray-900 dark:text-white">
            {isBase ? `Licença base — ${modulo.nome}` : modulo.nome}
          </p>
          <p className="text-xs text-gray-500 dark:text-slate-400">
            {modulo.codigo}
            {tipo === "contador" ? " · cobrado por unidade na licença" : ""}
          </p>
        </div>
        <div className="md:col-span-3">
          <label className="md:hidden text-xs text-gray-500 mb-1 block">Valor (R$)</label>
          <input
            type="text"
            inputMode="decimal"
            placeholder="0,00"
            value={precos[modulo.id] ?? ""}
            onChange={(e) => setPrecos((p) => ({ ...p, [modulo.id]: e.target.value }))}
            className="w-full px-3 py-2 rounded-xl border zen-card border-gray-300 dark:border-white/10 dark:bg-slate-950/40 dark:text-white"
          />
        </div>
        <div className="md:col-span-4">
          <label className="md:hidden text-xs text-gray-500 mb-1 block">Unidade</label>
          <select
            value={unidades[modulo.id] || unidadePadraoPreco(modulo.codigo, secao.licencaTipo)}
            onChange={(e) => setUnidades((u) => ({ ...u, [modulo.id]: e.target.value }))}
            className="w-full px-3 py-2 rounded-xl border zen-card border-gray-300 dark:border-white/10 dark:bg-slate-950/40 dark:text-white"
          >
            {UNIDADES_COBRANCA.map((u) => (
              <option key={u.value} value={u.value}>
                {u.label}
              </option>
            ))}
          </select>
        </div>
      </div>
    );
  };

  if (loading) {
    return <div className="p-6 text-center text-gray-500 dark:text-slate-400">Carregando...</div>;
  }

  return (
    <div className="max-w-4xl mx-auto">
      <form onSubmit={handleSubmit} className="zen-card rounded-2xl p-6 shadow-sm dark:backdrop-blur space-y-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
            {isEdit ? "Editar tabela de preço" : "Nova tabela de preço"}
          </h2>
          <p className="text-sm text-gray-500 dark:text-slate-400 mt-1">
            Preços por produto, no mesmo formato das licenças. Módulos com valor 0 não entram na tabela.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1 dark:text-slate-200">Nome</label>
            <input
              value={nome}
              onChange={(e) => setNome(e.target.value)}
              required
              className="w-full px-3 py-2.5 rounded-xl border zen-card dark:border-white/10 dark:bg-slate-950/40 dark:text-white"
            />
          </div>
          <div className="flex items-end">
            <label className="inline-flex items-center gap-2 text-sm dark:text-slate-200">
              <input type="checkbox" checked={ativo} onChange={(e) => setAtivo(e.target.checked)} />
              Tabela ativa
            </label>
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-medium mb-1 dark:text-slate-200">Dono da tabela</label>
            <select
              value={distribuidorId}
              onChange={(e) => setDistribuidorId(e.target.value)}
              className="w-full px-3 py-2.5 rounded-xl border zen-card dark:border-white/10 dark:bg-slate-950/40 dark:text-white"
            >
              <option value="">Zen — cobra o distribuidor</option>
              {distribuidores.map((d) => (
                <option key={d.id} value={d.id}>
                  {(d.nomeFantasia || d.razaoSocial)} — cobra as revendas dele
                </option>
              ))}
            </select>
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-medium mb-1 dark:text-slate-200">Descrição</label>
            <input
              value={descricao}
              onChange={(e) => setDescricao(e.target.value)}
              className="w-full px-3 py-2.5 rounded-xl border zen-card dark:border-white/10 dark:bg-slate-950/40 dark:text-white"
            />
          </div>
        </div>

        {blocos.map((bloco) => (
          <div
            key={bloco.secao.licencaTipo}
            className="zen-card rounded-2xl p-4 dark:bg-white/5 dark:border-white/10"
          >
            <h3 className="text-base font-semibold text-gray-900 dark:text-white">
              {bloco.secao.titulo}
            </h3>
            <p className="text-xs text-gray-500 dark:text-slate-400 mt-1 mb-4">
              {bloco.secao.subtitulo}
            </p>

            <div className="hidden md:grid md:grid-cols-12 gap-3 text-xs text-gray-500 dark:text-slate-400 mb-2 px-1">
              <div className="md:col-span-5">Item</div>
              <div className="md:col-span-3">Valor (R$)</div>
              <div className="md:col-span-4">Unidade de cobrança</div>
            </div>

            {bloco.linhas.map(renderLinha)}
          </div>
        ))}

        <div className="flex justify-end">
          <button
            type="submit"
            disabled={saving}
            className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-blue-600 text-white font-semibold text-sm hover:bg-blue-500 disabled:opacity-60"
          >
            <Save size={18} />
            {saving ? "Salvando..." : "Salvar tabela"}
          </button>
        </div>
      </form>
    </div>
  );
};

function inferLicencaTipoPorCodigo(codigo) {
  if (codigo === "ZENTAG_BASE") return "ZenTag";
  if (codigo === "ZENMOBILE_BASE") return "ZenMobile";
  if (codigo === "ZENPASS_BASE") return "ZenPass";
  if (codigo === "PDV_BASE" || codigo === "ADD_PDV_ADICIONAL" || codigo === "ADD_PDV_POS") {
    return "ZenPDV";
  }
  return "ZenGestao";
}

export default FormularioTabelaPreco;
